import { qrSvg } from './qrcode.js';
import { lang, ui } from '../config/i18n.js';

/**
 * The card that hands the city to a phone.
 *
 * Isidora is drawn at the size of the window it is given, and a window held
 * in one hand is a different reading of the same descent — so the only thing
 * worth building here is the shortest path from the screen on the desk to the
 * screen in the pocket: a code, the address under it in case the camera
 * refuses, and nothing else.
 *
 * The hard part is not the code, it is the address. A dev server answers on
 * `127.0.0.1`, which is the one address on the network that a phone can never
 * reach, so the build injects the machine's own LAN addresses (see
 * `vite.config.js`) and the card offers those instead. A built copy served
 * from anywhere real just shares where it already is.
 */

/** Ranked IPv4 addresses of this machine, injected at config time. */
const HOSTS = typeof __ISIDORA_HOSTS__ !== 'undefined' ? __ISIDORA_HOSTS__ : [];

const LOOPBACK = new Set(['localhost', '127.0.0.1', '::1', '[::1]', '0.0.0.0']);

/**
 * Every address this page could be opened at, best first. The language is
 * carried along so the phone opens the reading that is on screen here, and
 * `quality`/`inspect` are dropped — a phone resolves its own tier.
 *
 * `lan` says the addresses are this machine's own, which is the only case
 * where the phone has to be told to join the same network.
 */
export function shareTargets() {
  const loc = globalThis.location;
  if (!loc) return { urls: [], lan: false };

  const search = new URLSearchParams(loc.search);
  search.delete('quality');
  search.delete('inspect');
  search.delete('capture');
  search.set('lang', lang());
  const tail = `${loc.pathname}?${search}${loc.hash}`;

  if (!LOOPBACK.has(loc.hostname)) return { urls: [`${loc.origin}${tail}`], lan: false };

  const port = loc.port ? `:${loc.port}` : '';
  return { urls: HOSTS.map((h) => `${loc.protocol}//${h}${port}${tail}`), lan: true };
}

export class Share {
  /** @param {HTMLElement} root the overlay root */
  constructor(root) {
    this.root = root;
    this.on = false;
    this.index = 0;
    this.targets = [];
    this.lan = false;

    const el = document.createElement('div');
    el.className = 'share';
    el.innerHTML = `
      <div class="share__card" role="dialog" aria-modal="false" data-card>
        <div class="share__title" data-title></div>
        <div class="share__code" data-code></div>
        <div class="share__url" data-url></div>
        <div class="share__hint" data-hint></div>
        <div class="share__acts">
          <button class="share__act" type="button" data-other hidden></button>
          <button class="share__act" type="button" data-close></button>
        </div>
      </div>
    `;
    root.appendChild(el);

    this.el = {
      scrim: el,
      card: el.querySelector('[data-card]'),
      title: el.querySelector('[data-title]'),
      code: el.querySelector('[data-code]'),
      url: el.querySelector('[data-url]'),
      hint: el.querySelector('[data-hint]'),
      other: el.querySelector('[data-other]'),
      close: el.querySelector('[data-close]'),
    };

    this.el.scrim.addEventListener('click', (e) => {
      if (e.target === this.el.scrim) this.close();
    });
    this.el.close.addEventListener('click', () => this.close());
    this.el.other.addEventListener('click', () => {
      this.index = (this.index + 1) % Math.max(this.targets.length, 1);
      this.render();
    });
  }

  toggle() {
    if (this.on) this.close();
    else this.open();
  }

  open() {
    this.on = true;
    // Resolved on every opening: the language may have changed since the
    // last one, and so may the network this machine is on.
    const { urls, lan } = shareTargets();
    this.targets = urls;
    this.lan = lan;
    if (this.index >= this.targets.length) this.index = 0;
    this.render();
    this.el.scrim.classList.add('is-on');
  }

  close() {
    this.on = false;
    this.el.scrim.classList.remove('is-on');
  }

  /** Called when the language changes; only matters while the card is up. */
  retranslate() {
    if (this.on) this.open();
  }

  render() {
    const url = this.targets[this.index] ?? globalThis.location?.href ?? '';
    this.el.title.textContent = ui('shareTitle');
    this.el.code.innerHTML = qrSvg(url).svg;
    this.el.url.textContent = url;
    if (!this.targets.length) this.el.hint.textContent = ui('shareLocal');
    else if (this.lan) this.el.hint.textContent = `${ui('shareHint')} · ${ui('shareNetwork')}`;
    else this.el.hint.textContent = ui('shareHint');
    this.el.close.textContent = ui('shareClose');
    this.el.other.textContent = ui('shareOther');
    this.el.other.hidden = this.targets.length < 2;
  }

  dispose() {
    this.el.scrim.remove();
  }
}
